import { theme } from 'components/Theme'
import { CallTypeEnum, CallTypeToJobTypeEnum, UseCaseEnumToUseCaseIdEnum } from 'features/calls/types'
import { CoverageStatusEnum, ILevelOfSupport } from 'features/claims/types'
import { FaRobot } from 'react-icons/fa'
import { Badge } from '@outbound-ai/ursa'
import styled from 'styled-components'

export const formatLevelofSupport = (value: string) => {
  const levelofSupport = JSON.parse(localStorage.getItem('levelOfSupport')!)
  const levels = levelofSupport?.find((level: ILevelOfSupport) => level?.levelOfSupport === value)

  return (
    <Div>
      {!value ? (
        'Not Supported'
      ) : (
        <>
          {levels?.supportedOperations.map((operation: string, index: number) => (
            <Tags key={index}>
              {operation === 'FetchECS' && (
                <Badge variant="tag" type="success">
                  Fetch ECS
                </Badge>
              )}
              {operation === 'Get a Rep' && (
                <Badge variant="tag" type="info">
                  Get a Rep
                </Badge>
              )}
              {operation === 'FetchEBV' && (
                <Badge variant="tag" type="success">
                  Fetch EBV
                </Badge>
              )}
              {operation === 'FetchEOB' && (
                <Badge variant="tag" type="success">
                  Fetch EOB
                </Badge>
              )}
              {operation === 'FetchASC' && (
                <Badge variant="tag" type="success">
                  Fetch ASC
                </Badge>
              )}
              {operation === 'NotSupported' && 'Not Supported'}
            </Tags>
          ))}
        </>
      )}
    </Div>
  )
}

export const formatCoverageStatus = (value: string) => {
  return (
    <Div>
      {!value ? (
        <span className="span">Unverified</span>
      ) : (
        <>
          {value.toLowerCase() === CoverageStatusEnum.active && <span className="span green">Active</span>}
          {value.toLowerCase() === CoverageStatusEnum.inactive && <span className="span red">Inactive</span>}
          {value.toLowerCase() === CoverageStatusEnum.unknown && <span className="span">Unknown</span>}
        </>
      )}
    </Div>
  )
}

export const formatJobType = (value: CallTypeToJobTypeEnum, useCase: UseCaseEnumToUseCaseIdEnum) => {
  return (
    <Div>
      {value === CallTypeToJobTypeEnum.claimStatus ? (
        <Badge variant="tag" type="success">
          <FaRobot /> {formatCallTypeLabel(CallTypeEnum.unattended, useCase!)}
        </Badge>
      ) : value === CallTypeToJobTypeEnum.humanAgent ? (
        <Badge variant="tag" type="info">
          <FaRobot /> {formatCallTypeLabel(CallTypeEnum.humanAgent, useCase!)}
        </Badge>
      ) : value === CallTypeToJobTypeEnum.attendable ? (
        <Badge variant="tag" type="info">
          <FaRobot /> {formatCallTypeLabel(CallTypeEnum.attendable, useCase!)}
        </Badge>
      ) : value === CallTypeToJobTypeEnum.standard ? (
        <Badge variant="tag" type="grey">
          {formatCallTypeLabel(CallTypeEnum.standard, useCase!)}
        </Badge>
      ) : (
        '---'
      )}
    </Div>
  )
}

export const formatCallTypeLabel = (callType: CallTypeEnum, useCase: UseCaseEnumToUseCaseIdEnum) => {
  return callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.CSI
    ? 'Fetch ECS'
    : callType === CallTypeEnum.attendable && useCase === UseCaseEnumToUseCaseIdEnum.CSI
    ? 'Fetch ECS (attendable)'
    : callType === CallTypeEnum.humanAgent && useCase === UseCaseEnumToUseCaseIdEnum.CSI
    ? 'Get a Rep'
    : callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.EOB
    ? 'Fetch ECS + EOB'
    : callType === CallTypeEnum.humanAgent && useCase === UseCaseEnumToUseCaseIdEnum.EOB
    ? 'Get a Rep (EOB)'
    : callType === CallTypeEnum.attendable && useCase === UseCaseEnumToUseCaseIdEnum.EOB
    ? 'EOB (attendable)'
    : callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.EBV
    ? 'Fetch EBV'
    : callType === CallTypeEnum.humanAgent && useCase === UseCaseEnumToUseCaseIdEnum.EBV
    ? 'Get a Rep (EBV)'
    : callType === CallTypeEnum.standard && useCase === UseCaseEnumToUseCaseIdEnum.CSI
    ? 'Manual Call'
    : callType === CallTypeEnum.standard && useCase === UseCaseEnumToUseCaseIdEnum.EBV
    ? 'Manual Call'
    : callType === CallTypeEnum.attendable && useCase === UseCaseEnumToUseCaseIdEnum.WorkersComp
    ? "Workers' Comp (attendable)"
    : callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.WorkersComp
    ? "Workers' Comp (unattended)"
    : callType === CallTypeEnum.attendable && useCase === UseCaseEnumToUseCaseIdEnum.DirectLine
    ? 'Direct Line (attendable)'
    : callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.DirectLine
    ? 'Direct Line (unattended)'
    : callType === CallTypeEnum.unattended && useCase === UseCaseEnumToUseCaseIdEnum.ASC
    ? 'Fetch ASC'
    : callType === CallTypeEnum.humanAgent && useCase === UseCaseEnumToUseCaseIdEnum.ASC
    ? 'Fetch ASC (Get a Rep)'
    : '---'
}

const Tags = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxxs};
  :empty {
    display: none;
  }
`

const Div = styled.div`
  white-space: nowrap;
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .span {
    border-radius: ${({ theme }) => theme.util.pixelToRem(5)};
    ${({ theme }) => theme.typography.s.medium};
    font-weight: 500;
    padding: ${({ theme }) => theme.util.pixelToRem(5)} ${({ theme }) => theme.util.pixelToRem(5)};
  }

  .fetch {
    background-color: ${theme.colors.success.lightest};
    ${({ theme }) => theme.typography.s.medium};
    color: ${theme.colors.success.neutral};
    border: 1px solid ${theme.colors.success.lighter};
    > svg {
      margin-top: -2px;
      margin-right: 2px;
    }
    margin-right: 0.5rem;
  }

  .green {
    color: ${theme.colors.success.neutral};
    ${({ theme }) => theme.typography.s.normal};
  }

  .red {
    color: ${theme.colors.error.neutral};
    ${({ theme }) => theme.typography.s.normal};
  }

  .no {
    color: ${theme.colors.grey.neutral};
    ${({ theme }) => theme.typography.s.normal};
  }

  .info {
    background-color: ${theme.colors.info.lightest};
    ${({ theme }) => theme.typography.s.medium};
    padding: ${({ theme }) => theme.util.pixelToRem(5)} ${({ theme }) => theme.util.pixelToRem(15)}!important;
    color: ${theme.colors.info.neutral};
    border: 1px solid ${theme.colors.info.lighter};
    > svg {
      margin-top: -2px;
      margin-right: 2px;
    }
  }
  .fetchebv {
    background-color: ${theme.colors.success.lightest};
    ${({ theme }) => theme.typography.s.medium};
    color: ${theme.colors.success.neutral};
    border: 1px solid ${theme.colors.success.lighter};
    padding: ${({ theme }) => theme.util.pixelToRem(5)} ${({ theme }) => theme.util.pixelToRem(15)}!important;
    > svg {
      margin-top: -2px;
      margin-right: 2px;
    }
  }
  .grey {
    background-color: ${theme.colors.grey.lightest};
    ${({ theme }) => theme.typography.s.medium};
    padding: ${({ theme }) => theme.util.pixelToRem(5)} ${({ theme }) => theme.util.pixelToRem(15)};
    color: ${theme.colors.grey.neutral};
    border: 1px solid ${theme.colors.grey.lighter};
    > svg {
      margin-top: -2px;
      margin-right: 2px;
    }
  }
`
